import { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

import { DataEditor } from '@glideapps/glide-data-grid';

import type {
  DataEditorRef,
  Item,
  GridSelection,
  GridMouseEventArgs,
  HeaderClickedEventArgs,
  EditableGridCell,
  Highlight,
} from '@glideapps/glide-data-grid';

import { Portal } from '@simplywallst/ui-core';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { COLUMNS, COLUMN_IDS, THEME } from './constants';

import type { ColumnId, Lang } from './types';

import { drawHeaderCells, getDamagedRow, isCellValue } from './utils';
import type { CellValue } from './utils';

import type { useTableState } from './hooks/useTableState';

import { RowInsert } from './components/RowInsert';
import { DateFormatSelect } from './components/DateFormatSelect';

const TickerRematchOverlay = loadable(
  () => import('./components/TickerRematchOverlay'),
  {
    resolveComponent: (module) => module.TickerRematchOverlay,
  }
);

import {
  MenuCellRenderer,
  DateCellRenderer,
  TickerCellRenderer,
  SharePriceCellRenderer,
  SharesCellRenderer,
  TotalCostCellRenderer,
} from '../CustomCell';

import { isKindOfCustomCell } from '../CustomCell/utils';

import { isDateCell, getDateCellTemplate } from '../CustomCell/Date';

import { GlobalStyle } from './styled';
import { useTickerRematch } from './hooks';

const dateColIndex = COLUMN_IDS.indexOf('date');

// NOTE: Don't change the portal id,
// Glide Grid implicitly expects the portal id to be 'portal'
// in order to render the overlay components with the Grid
const PORTAL_ID = 'portal';

type ActivatedCell = { cell: Item; id: ColumnId };
type CustomRenderers = readonly [
  typeof MenuCellRenderer,
  typeof DateCellRenderer,
  typeof TickerCellRenderer,
  typeof SharePriceCellRenderer,
  typeof SharesCellRenderer,
  typeof TotalCostCellRenderer,
];

type Props = {
  customRenderers?: CustomRenderers;
  scrollToIndex?: number;
  highlightRegions?: Highlight[];
  selectedLanguage: Lang;
  gridSelection?: GridSelection;
  setSelectedLanguage: (lang: Lang) => void;
  onUpdate?: (prev?: CellValue, next?: CellValue) => void;
  setGridSelection: (selection?: GridSelection) => void;
  onCellActivated?: (cell: ActivatedCell) => void;
  onFinishedEditing?: () => void;
} & ReturnType<typeof useTableState>;

export const Table = ({
  customRenderers,
  scrollToIndex,
  highlightRegions = [],
  selectedLanguage,
  gridSelection,
  onUpdate,
  setSelectedLanguage,
  setGridSelection,
  onCellActivated,
  onFinishedEditing,
  ...tableState
}: Props) => {
  const { trackEvent } = useTracking();

  const { isMobile, isTablet } = useUserAgent();
  const gridRef = useRef<DataEditorRef | null>(null);

  const [headerItem, setHeaderItem] = useState<GridMouseEventArgs | null>(null);
  const [hoveredItem, setHoveredItem] = useState<GridMouseEventArgs | null>(
    null
  );

  const {
    rowCount,
    getColumn,
    getCellValue,
    setCellValue,
    clearCellValue,
    clearBatchCellValues,
    deleteRows,
    insertRow,
    setRowCount,
  } = tableState;

  useEffect(() => {
    if (scrollToIndex) {
      gridRef.current?.scrollTo(0, scrollToIndex, 'vertical', 0, 100, {
        vAlign: 'start',
      });
    }
  }, [scrollToIndex]);

  const repaintRow = useCallback((rowIndex: number) => {
    gridRef.current?.updateCells(getDamagedRow(rowIndex));
  }, []);

  const {
    tickerRematchState,
    validateTickerCellRematch,
    rematchTickerCells,
    resetTickerRematchState,
  } = useTickerRematch({
    rematchCell: setCellValue,
    onRematchCell: repaintRow,
    getTickerColumn: () => getColumn(COLUMN_IDS.indexOf('ticker')),
  });

  const repaintBatchRows = useCallback(
    (fromRow: number, toRow: number) => {
      for (let rowIndex = fromRow; rowIndex <= toRow; rowIndex++) {
        repaintRow(rowIndex);
      }
    },
    [repaintRow]
  );

  // Overrides the default cell level paste behavior when returning false
  // We override the default behavior to handle multiple pasted rows that exceed the current rowCount
  const handlePaste = useCallback(
    (item: Item, values: readonly (readonly string[])[]) => {
      const [_, rowIndex] = item;
      if (rowIndex + values.length > rowCount) {
        setRowCount(rowIndex + values.length);
        gridRef.current?.emit('paste');
        return false;
      }
      return true;
    },
    [rowCount, setRowCount]
  );

  const debouncedTickerCellRematchValidation = useMemo(
    () => debounce(validateTickerCellRematch, 100),
    [validateTickerCellRematch]
  );

  const handleCellEdited = useCallback(
    (cell: Item, newCell: EditableGridCell) => {
      const [_, rowIndex] = cell;
      const oldCell = getCellValue(cell);

      const oldCellValue =
        isKindOfCustomCell(oldCell) && 'value' in oldCell.data
          ? oldCell.data.value
          : null;

      const newCellValue =
        isKindOfCustomCell(newCell) && 'value' in newCell.data
          ? newCell.data.value
          : null;

      setCellValue(cell, newCell, () => {
        repaintRow(rowIndex);
        debouncedTickerCellRematchValidation(cell, oldCell, newCell);

        if (isCellValue(newCellValue) && isCellValue(oldCellValue)) {
          onUpdate?.(newCellValue, oldCellValue);
          return;
        }

        onUpdate?.();
      });
    },
    [
      getCellValue,
      setCellValue,
      repaintRow,
      onUpdate,
      debouncedTickerCellRematchValidation,
    ]
  );

  // Edit view validated count / populated count isn't populated until user interacts with the table. This useffect ensures that the initial populated count is updated
  useEffect(() => {
    onUpdate?.();
  }, [onUpdate]);

  // Overrides the default cell level delete behavior when returning false
  // We override the default behavior to handle the repainting of cells that depend on each other
  const handleDelete = useCallback(
    (selected: GridSelection) => {
      const { rows, current } = selected;
      const rowList = rows.toArray();

      if (rowList.length === 0 && current?.cell) {
        const { cell, range } = current;
        const [_, rowIndex] = cell;
        if (range.width === 1 && range.height === 1) {
          // Single cell delete
          clearCellValue(cell);
          repaintRow(rowIndex);
        } else {
          // Range batch cell delete
          const fromRow = range.y;
          const toRow = range.y + range.height - 1;
          const fromColumn = range.x;
          const toColumn = range.x + range.width - 1;
          clearBatchCellValues({
            fromRow,
            toRow,
            fromColumn,
            toColumn,
          });
          repaintBatchRows(fromRow, toRow);
        }

        onUpdate?.();

        return false;
      }

      deleteRows(rowList);
      setGridSelection(undefined);

      onUpdate?.();

      return false;
    },
    [
      deleteRows,
      setGridSelection,
      onUpdate,
      clearCellValue,
      repaintRow,
      clearBatchCellValues,
      repaintBatchRows,
    ]
  );

  const handleDateFormatChange = useCallback(
    (lang: Lang) => {
      trackEvent({
        modifier: 'changeDateFormat',
        action: 'select',
        dataFormat: lang,
      });
      setSelectedLanguage(lang);
      // Repaint the header cells
      repaintRow(-1);
      // Update the date format for all date cells
      const colIndex = dateColIndex;
      const dateColumn = getColumn(colIndex);

      if (typeof dateColumn === 'undefined') return [];

      const updatedCells: { cell: Item }[] = [];

      dateColumn.forEach((cell, rowIndex) => {
        if (isKindOfCustomCell(cell) && isDateCell(cell)) {
          setCellValue([colIndex, rowIndex], {
            ...cell,
            ...getDateCellTemplate({ ...cell.data, lang }),
            lastUpdated: performance.now(),
          });
          updatedCells.push({ cell: [colIndex, rowIndex] });
        }
      });
      // Repaint the cells updated within the date column
      gridRef.current?.updateCells(updatedCells);
      //
      setHeaderItem(null);
    },
    [trackEvent, setSelectedLanguage, repaintRow, getColumn, setCellValue]
  );

  const handleHeaderClicked = useCallback(
    (colIndex: number, cell: HeaderClickedEventArgs) => {
      if (colIndex === dateColIndex) {
        trackEvent({ modifier: 'changeDateFormat', action: 'click' });
        setHeaderItem(cell);
      }
    },
    [trackEvent]
  );

  const handleDrawHeader = useCallback(
    (args) => drawHeaderCells(args, selectedLanguage),
    [selectedLanguage]
  );

  const handleCellActivated = useCallback(
    (cell: Item) => {
      const [colIndex] = cell;
      const id = COLUMN_IDS[colIndex];
      if (id) {
        onCellActivated?.({
          cell,
          id,
        });
      }
    },
    [onCellActivated]
  );

  // here we aggregate the highlight regions from multiple sources.
  // atm its the table level validation and the ticker cell rematching.
  // this way we don't need to share a common state between these sources.
  const aggregateHighlightRegions = useMemo(
    () => [
      ...highlightRegions,
      ...(tickerRematchState?.highlightRegions ?? []),
    ],
    [highlightRegions, tickerRematchState?.highlightRegions]
  );

  return (
    <div>
      <GlobalStyle />
      <DataEditor
        ref={gridRef}
        width="100%"
        height="100%"
        rows={rowCount}
        smoothScrollX={true}
        smoothScrollY={isMobile || isTablet}
        customRenderers={
          customRenderers ?? [
            MenuCellRenderer,
            DateCellRenderer,
            TickerCellRenderer,
            SharePriceCellRenderer,
            SharesCellRenderer,
            TotalCostCellRenderer,
          ]
        }
        rowHeight={54}
        headerHeight={54}
        rowMarkers="clickable-number"
        columns={COLUMNS}
        minColumnWidth={170}
        maxColumnWidth={250}
        getCellContent={getCellValue}
        getCellsForSelection={true}
        onCellEdited={handleCellEdited}
        onPaste={handlePaste}
        onDelete={handleDelete}
        onCellActivated={handleCellActivated}
        onFinishedEditing={onFinishedEditing}
        fillHandle={true}
        allowedFillDirections="vertical"
        onGridSelectionChange={setGridSelection}
        gridSelection={gridSelection}
        onItemHovered={setHoveredItem}
        highlightRegions={aggregateHighlightRegions}
        onHeaderClicked={handleHeaderClicked}
        drawHeader={handleDrawHeader}
        theme={THEME}
      />
      <Portal portalId={PORTAL_ID}>
        <RowInsert onClick={insertRow} cell={hoveredItem} />
        {headerItem && headerItem.kind === 'header' && (
          <DateFormatSelect
            onClick={handleDateFormatChange}
            onClose={() => setHeaderItem(null)}
            lang={selectedLanguage}
            cell={headerItem}
          />
        )}
        {tickerRematchState && (
          <TickerRematchOverlay
            cell={
              gridRef.current?.getBounds(...tickerRematchState.cell) ?? null
            }
            oldValue={tickerRematchState.oldValue}
            newValue={tickerRematchState.newValue}
            matchRowCount={tickerRematchState.matchingRows.length}
            onConfirm={() => {
              trackEvent({
                subject: 'rematchTicker',
                modifier: 'confirm',
                action: 'click',
                rematchTickerCount: tickerRematchState.matchingRows.length,
              });
              rematchTickerCells();
            }}
            onCancel={() => {
              trackEvent({
                subject: 'rematchTicker',
                modifier: 'cancel',
                action: 'click',
                rematchTickerCount: tickerRematchState.matchingRows.length,
              });
              resetTickerRematchState();
            }}
          />
        )}
      </Portal>
    </div>
  );
};
